import { useState } from 'react';
import { motion } from 'motion/react';
import styled, { css } from 'styled-components';
import { space } from 'styled-system';
import { useRouter } from '~/utils/navigation';
import { LabelBold, LabelRegular, Caption, Box } from '@comeon/ui';

import Icon, { ICON_BOTTOM, ICON_INFO } from '~/components/icon/index.js';
import theme from 'styled-theming';

const variants = {
	hidden: { opacity: 0, y: '-10px', transitionEnd: { display: 'none' } },
	visible: { opacity: 1, y: 0, scale: 1, display: 'block' }
};

const variantsRotate = {
	down: { rotate: '0deg', y: ['0px', '-2px', '2px', '0px'] },
	up: { rotate: '180deg', y: ['0px', '2px', '-2px', '0px'] }
};

const dataAt = props => ({
	'data-at': props.dataAt
});

const Wrapper = styled.div`
	border-radius: 3px;
	background-color: var(--link-list, rgba(var(--base-10), 1));
	padding: 0;
	margin-top: 0;
	margin-bottom: 0;
	${space}
`;

const MotionIconWrapper = styled(motion.span)`
	width: 24px;
	height: 24px;
`;

const Flex = styled.div.attrs(dataAt)`
	align-items: center;
	cursor: pointer;
	display: flex;
	justify-content: space-between;
	${space}
	svg {
		height: 24px;
		width: 24px;
	}
`;

const ContentWrapper = motion.create(Box);

const CustomCaption = styled(Caption)`
	white-space: pre-line;
`;

export const CollapseGroup = styled.div`
	& > div {
		&:first-child:not(:only-child) {
			border-radius: 3px 3px 0 0;
		}
		&:last-child:not(:only-child) {
			border-radius: 0 0 3px 3px;
		}
		&:not(:last-child):not(:only-child) {
			margin-bottom: 1px;
		}
	}
`;

const IconWrapper = styled.div`
	svg {
		${theme('brand', {
			comeon: css`
				fill: rgba(var(--primary-accent-green), 1);
			`,
			suomikasino: css`
				fill: rgba(var(--primary-accent-green), 1);
			`,
			getlucky: css`
				fill: rgba(var(--primary-accent-orange), 1);
			`,
			mobilebet: css`
				fill: rgba(var(--primary-accent-green), 1);
			`,
			nopeampi: css`
				fill: rgba(var(--primary-accent-blue), 1);
			`,
			galaksino: css`
				fill: rgba(var(--primary-accent-purple-light), 1);
			`
		})}
	}
`;

export function Collapse({
	label,
	text,
	dataAt: someDataAt,
	value,
	infoIcon = false,
	icon,
	children,
	type = 'collapse',
	onClick,
	link,
	returnLink,
	...rest
}) {
	const [collapse, setCollapse] = useState(true);
	const router = useRouter();

	const toggleCollapse = () => {
		switch (type) {
			case 'link':
				// TODO (navigation) - use search params or redux for state
				router.replace(link, { state: { returnLink } });
				break;
			case 'button':
				onClick();
				break;
			default:
				setCollapse(!collapse);
				break;
		}
	};

	return (
		<Wrapper {...rest}>
			<Flex p="6" onClick={toggleCollapse} dataAt={someDataAt}>
				<LabelBold m={0} className="collapse-title">
					{label}
				</LabelBold>

				{(value || value === 0) && (
					<LabelRegular my={1} className="collapse-value">
						{value}
					</LabelRegular>
				)}
				{infoIcon && (
					<IconWrapper>
						<Icon type={ICON_INFO} className="collapse__info-icon" />
					</IconWrapper>
				)}
				{!infoIcon && (
					<MotionIconWrapper
						initial="down"
						variants={variantsRotate}
						animate={collapse ? 'down' : 'up'}
						transition={{
							x: { type: 'spring', stiffness: 100, damping: 100 }
						}}
					>
						{icon ? (
							<IconWrapper>{icon}</IconWrapper>
						) : (
							<Icon className="collapse__continue-icon" type={ICON_BOTTOM} />
						)}
					</MotionIconWrapper>
				)}
			</Flex>
			<ContentWrapper initial="hidden" variants={variants} animate={collapse ? 'hidden' : 'visible'} p="6" pt="0">
				{text && <CustomCaption>{text}</CustomCaption>}
				{children}
			</ContentWrapper>
		</Wrapper>
	);
}
